/**
 * Animates the problem-solution section when it scrolls into view:
 *   1. The center line grows from top to bottom
 *   2. Problem/Solution pairs appear one by one (opacity + translateY)
 *   3. The currently appearing pair has a filled shield icon;
 *      all previously revealed pairs switch to outlined
 *   4. Final state: all pairs visible, all shields outlined
 *
 * Animations fire only once (IntersectionObserver unobserves after trigger).
 * Respects prefers-reduced-motion: if set, all pairs are shown immediately.
 */
(function () {
  'use strict';

  /** Delay (ms) before the line starts growing after section enters viewport */
  const LINE_START_DELAY = 150;

  /** Duration (ms) the line animation takes (matches CSS transition) */
  const LINE_DURATION = 1000;

  /** Delay (ms) between each pair reveal */
  const ITEM_STAGGER = 400;

  /** Delay (ms) after line starts before first pair appears */
  const FIRST_ITEM_DELAY = 350;

  /** Mobile breakpoint – must match CSS */
  const MOBILE_BREAKPOINT = 750;

  /**
   * Initialise animation for a single .problem-solution section.
   * @param {HTMLElement} section – the .problem-solution element with [data-problem-solution-animate]
   */
  function initProblemSolutionAnimation(section) {
    const timeline = section.querySelector('.problem-solution__timeline');
    if (!timeline) return;

    const pairs = Array.from(timeline.querySelectorAll('.problem-solution__pair'));
    if (pairs.length === 0) return;

    // Collect node elements for shield icon state toggling
    const nodes = pairs.map(function (pair) {
      return pair.querySelector('.problem-solution__node');
    });

    // On mobile: no animation – show everything immediately
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      // Remove the data attribute so CSS doesn't hide anything
      section.removeAttribute('data-problem-solution-animate');
      return;
    }

    // If user prefers reduced motion, show everything immediately and bail out
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      section.classList.add('problem-solution--line-visible');
      pairs.forEach(function (pair) {
        pair.classList.add('problem-solution__pair--visible');
      });
      return;
    }

    var hasAnimated = false;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !hasAnimated) {
            hasAnimated = true;
            observer.unobserve(section);
            runAnimation(section, pairs, nodes);
          }
        });
      },
      {
        // Trigger when at least 15% of the timeline is visible, offset 50px from bottom
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.15,
      }
    );

    observer.observe(section);
  }

  /**
   * Run the full animation sequence.
   */
  function runAnimation(section, pairs, nodes) {
    // Step 1: Start the line growth
    setTimeout(function () {
      section.classList.add('problem-solution--line-visible');
    }, LINE_START_DELAY);

    // Step 2 & 3: Reveal pairs one by one
    pairs.forEach(function (pair, index) {
      var delay = LINE_START_DELAY + FIRST_ITEM_DELAY + index * ITEM_STAGGER;

      setTimeout(function () {
        // Remove active state from previous node
        if (index > 0 && nodes[index - 1]) {
          nodes[index - 1].classList.remove('problem-solution__node--active');
        }

        // Set current node as active (filled shield)
        if (nodes[index]) {
          nodes[index].classList.add('problem-solution__node--active');
        }

        // Reveal the pair
        pair.classList.add('problem-solution__pair--visible');

        // If this is the last pair, remove active state after its transition completes
        if (index === pairs.length - 1) {
          setTimeout(function () {
            if (nodes[index]) {
              nodes[index].classList.remove('problem-solution__node--active');
            }
          }, 600); // slightly after the CSS transition (400ms) finishes
        }
      }, delay);
    });
  }

  /**
   * Initialise all problem-solution sections on the page.
   */
  function init() {
    var sections = document.querySelectorAll('[data-problem-solution-animate]');
    sections.forEach(initProblemSolutionAnimation);
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-initialise when sections are added / reloaded
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target.querySelector('[data-problem-solution-animate]');
      if (section) {
        // In design mode, show everything immediately (no animation)
        section.classList.add('problem-solution--line-visible');
        var pairs = section.querySelectorAll('.problem-solution__pair');
        pairs.forEach(function (pair) {
          pair.classList.add('problem-solution__pair--visible');
        });
      }
    });
  }
})();
